import type { BusinessIntegration } from '../types';
import plaidLogo from '@/assets/source-logos/plaid-logo.svg';
import cloverLogo from '@/assets/source-logos/clover-logo.svg';
import toastLogo from '@/assets/source-logos/toast-logo.svg';
import lightspeedLogo from '@/assets/source-logos/lightspeed-logo.jpeg';
import squareLogo from '@/assets/source-logos/square-logo.svg';
import shopifyLogo from '@/assets/source-logos/shopify-logo.svg';
import stripeLogo from '@/assets/source-logos/stripe-logo.svg';
import paypalLogo from '@/assets/source-logos/paypal-logo.svg';
import quickbooksLogo from '@/assets/source-logos/quickbooks-logo.svg';
import xeroLogo from '@/assets/source-logos/xero-logo.svg';
import mrpeasyLogo from '@/assets/source-logos/mrpeasy-logo.svg';
import webhookLogo from '@/assets/source-logos/webhook-logo.svg';
import inboundEmailLogo from '@/assets/source-logos/inbound-email-logo.svg';
import googleDriveLogo from '@/assets/source-logos/google-drive-logo.svg';
import mysqlLogo from '@/assets/source-logos/mysql-logo.svg';

export const AVAILABLE_INTEGRATIONS: BusinessIntegration[] = [

  // Financial Integrations
  {
    id: 'plaid',
    name: 'Plaid',
    type: 'plaid',
    category: 'financial',
    description: 'Connect to thousands of banks and financial institutions for automatic transaction import',
    logo: plaidLogo,
    isConnected: false,
    features: [
      'Connect to 12,000+ financial institutions',
      'Real-time transaction sync',
      'Enhanced transaction categorization',
      'Multi-account support',
      'Incremental updates for efficiency',
      'Secure OAuth connection',
    ],
    setupFields: [], // Plaid uses Link OAuth flow
    status: 'available',
    customSetupComponent: 'PlaidLinkSetup',
  },

  // Point of Sale Integrations
  {
    id: 'clover',
    name: 'Clover POS',
    type: 'clover',
    category: 'pos',
    description: 'Sync orders and transaction details from your Clover point-of-sale system',
    logo: cloverLogo,
    isConnected: false,
    features: [
      'Real-time order sync',
      'Line item details',
      'Payment processing data',
      'Customer information',
      'Staff tracking',
      'Inventory insights',
    ],
    setupFields: [
      {
        key: 'merchantId',
        label: 'Merchant ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Clover Merchant ID',
        description: 'Find this in your Clover dashboard under Setup > Merchants',
        validation: {
          pattern: '^[A-Z0-9]{13}$',
          minLength: 13,
          maxLength: 13,
        },
      },
      {
        key: 'apiToken',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your Clover API token',
        description: 'Generate this in your Clover dashboard under Setup > API Tokens',
        validation: {
          minLength: 32,
        },
      },
      {
        key: 'environment',
        label: 'Environment',
        type: 'select',
        required: true,
        description: 'Select your Clover environment',
        options: [
          { value: 'production', label: 'Production' },
          { value: 'sandbox', label: 'Sandbox (Testing)' },
        ],
      },
    ],
    status: 'available',
  },

  {
    id: 'toast',
    name: 'Toast POS',
    type: 'toast',
    category: 'pos',
    description:
      'Sync orders, checks and menu-item sales from your Toast restaurant, and query your live menu and stock on demand',
    logo: toastLogo,
    isConnected: false,
    features: [
      'Net sales that match your Toast reports',
      'Business-date reporting that respects your closeout hour',
      'Menu item and sales category detail',
      'Dining option, revenue center and server breakdowns',
      'Tips, discounts, service charges and refunds',
      'Live menu, stock and staff lookups',
    ],
    setupFields: [
      {
        key: 'restaurantGuid',
        label: 'Restaurant GUID',
        type: 'text',
        required: true,
        placeholder: 'e.g. 8a1b2c3d-4e5f-6789-abcd-ef0123456789',
        description:
          'The Toast location to connect. Find it in Toast Web under Toast Partner Integrations, or in the URL of your restaurant page.',
        validation: {
          pattern: '^[0-9a-fA-F-]{36}$',
          minLength: 36,
          maxLength: 36,
        },
      },
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'password',
        required: true,
        placeholder: 'Enter your Toast API client ID',
        description:
          'Toast Web → Toast Partner Integrations → Toast API access. Requires the Manage Integrations permission for this location.',
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        required: true,
        placeholder: 'Enter your Toast API client secret',
        description: 'Shown only once, when you create the standard API access credentials.',
      },
      {
        key: 'environment',
        label: 'Environment',
        type: 'select',
        required: false,
        description: 'Leave on Production unless Toast gave you sandbox credentials',
        options: [
          { value: 'production', label: 'Production' },
          { value: 'sandbox', label: 'Sandbox (Testing)' },
        ],
      },
      {
        key: 'apiHostname',
        label: 'Sandbox API Hostname',
        type: 'text',
        required: false,
        placeholder: 'Leave blank for production',
        description:
          'Only for sandbox connections — the hostname issued by the Toast integrations team.',
      },
      {
        key: 'syncStartDate',
        label: 'Sync Start Date',
        type: 'date',
        required: false,
        description: 'Import orders from this date onwards. Defaults to the last 365 days.',
      },
    ],
    status: 'available',
    docsUrl: 'https://doc.toasttab.com/doc/devguide/devApiAccessUserGuide.html',
  },

  {
    id: 'lightspeed-k',
    name: 'Lightspeed Restaurant',
    type: 'lightspeed-k',
    category: 'pos',
    description:
      'Sync sales, line items, modifiers and payments from your Lightspeed K-Series restaurant',
    logo: lightspeedLogo,
    isConnected: false,
    features: [
      'Net sales after discounts, excluding tax and tips',
      'Item, modifier and accounting-group detail',
      'Item cost prices for gross-margin analysis',
      'Covers, for average spend per guest',
      'Order profile, revenue center and staff breakdowns',
      'Refunds and voids linked back to the original sale',
    ],
    // OAuth authorization-code flow — nothing for the user to paste. The
    // callback lands on a location picker rather than a success page.
    setupFields: [],
    status: 'available',
    docsUrl: 'https://api-portal.lsk.lightspeed.app/',
  },

  {
    id: 'square',
    name: 'Square POS',
    type: 'square',
    category: 'pos',
    description: 'Connect your Square point-of-sale system for transaction analysis',
    logo: squareLogo,
    isConnected: false,
    features: [
      'Transaction sync',
      'Item-level analytics',
      'Payment details',
      'Location-based reporting',
    ],
    setupFields: [
      {
        key: 'applicationId',
        label: 'Application ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Square Application ID',
        description: 'Found in your Square Developer Dashboard',
      },
      {
        key: 'accessToken',
        label: 'Access Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your Square Access Token',
        description: 'Generate this in your Square Developer Dashboard',
      },
      {
        key: 'environment',
        label: 'Environment',
        type: 'select',
        required: true,
        options: [
          { value: 'production', label: 'Production' },
          { value: 'sandbox', label: 'Sandbox' },
        ],
      },
    ],
    status: 'coming_soon',
  },

  // E-commerce Integrations
  {
    id: 'shopify',
    name: 'Shopify',
    type: 'shopify',
    category: 'ecommerce',
    description: 'Sync orders and customer data from your Shopify store',
    logo: shopifyLogo,
    isConnected: false,
    features: [
      'Order synchronization',
      'Product analytics',
      'Customer insights',
      'Revenue tracking',
    ],
    setupFields: [
      {
        key: 'shopDomain',
        label: 'Shop Domain',
        type: 'text',
        required: true,
        placeholder: 'your-shop.myshopify.com',
        description: 'Your Shopify store domain',
      },
      {
        key: 'accessToken',
        label: 'Access Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your Shopify access token',
        description: 'Private app access token from your Shopify admin',
      },
    ],
    status: 'coming_soon',
  },

  // Payment Processing
  {
    id: 'stripe',
    name: 'Stripe',
    type: 'stripe',
    category: 'business',
    description: 'Analyze payment data and customer transactions from Stripe',
    logo: stripeLogo,
    isConnected: false,
    features: [
      'Payment analytics',
      'Subscription tracking',
      'Failed payment insights',
      'Customer lifetime value',
    ],
    setupFields: [
      {
        key: 'secretKey',
        label: 'Secret Key',
        type: 'password',
        required: true,
        placeholder: 'sk_live_... or sk_test_...',
        description: 'Your Stripe secret key',
      },
      {
        key: 'webhookSecret',
        label: 'Webhook Secret',
        type: 'password',
        required: false,
        placeholder: 'whsec_...',
        description: 'Optional: For real-time updates',
      },
    ],
    status: 'coming_soon',
  },

  {
    id: 'paypal',
    name: 'PayPal',
    type: 'paypal',
    category: 'business',
    description: 'Monitor PayPal transactions and business analytics',
    logo: paypalLogo,
    isConnected: false,
    features: [
      'Transaction monitoring',
      'Dispute tracking',
      'Fee analysis',
      'Cross-border insights',
    ],
    setupFields: [
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your PayPal Client ID',
        description: 'From your PayPal Developer Dashboard',
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        required: true,
        placeholder: 'Enter your PayPal Client Secret',
        description: 'From your PayPal Developer Dashboard',
      },
      {
        key: 'environment',
        label: 'Environment',
        type: 'select',
        required: true,
        options: [
          { value: 'live', label: 'Live' },
          { value: 'sandbox', label: 'Sandbox' },
        ],
      },
    ],
    status: 'coming_soon',
  },

  // Accounting Integrations
  {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    type: 'quickbooks',
    category: 'accounting',
    description: 'Sync accounting data and financial records from QuickBooks',
    logo: quickbooksLogo,
    isConnected: false,
    features: [
      '14 transaction types supported',
      'Invoice & bill tracking',
      'Payment synchronization',
      'Line item details',
      'Real-time updates via webhooks',
      'Automatic categorization',
    ],
    setupFields: [], // QuickBooks uses OAuth flow
    // Superseded by QuickBooks Live. Entry stays so already-connected sources
    // still resolve a name and logo; `deprecated` keeps it out of the catalog.
    status: 'deprecated',
  },

  {
    id: 'quickbooks-online-live',
    name: 'QuickBooks Live',
    type: 'quickbooks-online-live',
    category: 'accounting',
    description:
      'Query QuickBooks on demand without copying data. Always-fresh answers — no sync, no warehouse, no drift. Use this when you want live answers and the synced version when you want analytics over historical transactions.',
    logo: quickbooksLogo,
    isConnected: false,
    features: [
      'Live API access — no data ingestion or duplication',
      'Always-fresh answers, no sync drift',
      '26 read-only tools: entity lookups, vendor reports, SQL-like queries',
      'P&L, Balance Sheet, Cash Flow, Aged AR/AP, Trial Balance, General Ledger',
      'Read-only: no write endpoints exposed',
      'Coexists with the synced QuickBooks integration',
    ],
    setupFields: [], // OAuth flow — same Intuit app as synced
    status: 'available',
  },

  {
    id: 'xero',
    name: 'Xero',
    type: 'xero',
    category: 'accounting',
    description: 'Sync invoices, payments, credit notes, bank transactions, and journals from Xero',
    logo: xeroLogo,
    isConnected: false,
    features: [
      '8 transaction types supported',
      'Sales invoices & purchase bills',
      'Payment tracking',
      'Credit note sync',
      'Bank transaction import',
      'Manual journal support',
      'Multi-org tenant support',
      'Automatic tax normalization',
    ],
    setupFields: [], // Xero uses OAuth flow
    status: 'available',
  },

  // ERP Integrations
  {
    id: 'mrpeasy',
    name: 'MRPEasy',
    type: 'mrpeasy',
    category: 'erp',
    description: 'Sync orders and invoices from MRPEasy for historical analysis, and query live reference data (stock, BOMs, vendors) on demand — one connection, both.',
    logo: mrpeasyLogo,
    isConnected: false,
    features: [
      'Purchase, customer, manufacturing & RMA orders + sales invoices synced for analytics',
      'Line item details',
      'Incremental sync via change detection',
      'Historical data import (up to 2 years)',
      'Live reference lookups: on-hand stock, BOM recipes, vendors, units',
      'One connection serves both the warehouse and live answers',
    ],
    setupFields: [
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your MRPEasy API key',
        description: 'Found in MRPEasy: Settings > Integration > API access',
      },
      {
        key: 'apiSecret',
        label: 'API Secret',
        type: 'password',
        required: true,
        placeholder: 'Enter your MRPEasy API secret',
        description: 'Found in MRPEasy: Settings > Integration > API access',
      },
    ],
    status: 'available',
  },

  // Federated database connections — read-only, just-in-time queries against
  // a customer-owned database. No data is ingested or stored locally.
  {
    id: 'mysql',
    name: 'MySQL',
    type: 'mysql',
    category: 'erp',
    description:
      'Connect a read-only MySQL database. Zamino queries on demand without copying data — your data stays in your database.',
    logo: mysqlLogo,
    isConnected: false,
    features: [
      'Read-only — INSERT/UPDATE/DELETE rejected at the parser',
      'Schema discovery on connect',
      'Per-database wiki of business meaning + query templates',
      'No data ingestion or duplication',
      'Supports MySQL 5.7.4+ and 8.0+',
    ],
    setupFields: [],
    status: 'available',
    customSetupComponent: 'MySQLConnectionSetup',
  },

  // Webhook Integrations
  {
    id: 'webhook-custom',
    name: 'Custom Webhook',
    type: 'webhook',
    category: 'business',
    description: 'Connect any webhook-enabled service to Zamino with flexible field mapping',
    logo: webhookLogo,
    isConnected: false,
    features: [
      'Universal webhook receiver',
      'Flexible field mapping',
      'Sample payload capture',
      'Custom data transformations',
      'Real-time transaction creation',
      'No coding required',
    ],
    setupFields: [], // Webhook uses custom wizard flow
    status: 'available',
    customSetupComponent: 'WebhookSetup',
  },

  {
    id: 'inbound_file',
    name: 'Inbound File Feed',
    type: 'inbound_file',
    category: 'business',
    description: 'Email recurring exports (timesheets, sales reports, invoices, statements) — we learn the format once, you approve it, every future file imports automatically.',
    logo: inboundEmailLogo,
    isConnected: false,
    features: [
      'CSV, Excel, PDF and Word — or the email body',
      'AI learns the format — you approve it',
      'Re-sent files update, never duplicate',
      'Format changes are held, never guessed at',
    ],
    setupFields: [],
    status: 'available',
    customSetupComponent: 'InboundFileSetup',
  },

  // Document Integrations
  {
    id: 'google_drive',
    name: 'Google Drive',
    type: 'google_drive',
    category: 'document',
    description: 'Connect your Google Drive to search and analyze business documents, contracts, spreadsheets, and more',
    logo: googleDriveLogo,
    isConnected: false,
    features: [
      'Folder-scoped document sync',
      'PDF, DOCX, XLSX, PPTX support',
      'Google Docs & Sheets export',
      'Full-text semantic search',
      'Automatic document chunking',
      'Secure OAuth2 connection',
    ],
    setupFields: [], // Uses OAuth flow
    status: 'coming_soon',
  },
];

// Category metadata
export const INTEGRATION_CATEGORIES = {
  financial: {
    name: 'Financial Services',
    description: 'Banks, credit cards, and financial institutions',
    icon: '🏦',
  },
  pos: {
    name: 'Point of Sale',
    description: 'Restaurant and retail POS systems',
    icon: '🏪',
  },
  ecommerce: {
    name: 'E-commerce',
    description: 'Online stores and marketplaces',
    icon: '🛒',
  },
  business: {
    name: 'Business Tools',
    description: 'Payment processors and business software',
    icon: '💼',
  },
  accounting: {
    name: 'Accounting',
    description: 'Accounting and bookkeeping software',
    icon: '📊',
  },
  erp: {
    name: 'ERP Systems',
    description: 'Enterprise resource planning and manufacturing systems',
    icon: '🏭',
  },
  document: {
    name: 'Documents',
    description: 'Document storage and knowledge base integrations',
    icon: '📄',
  },
} as const;